import { z } from 'zod'
import { UserPreferencesSchema } from '@/core/infrastructure/schemas/users/preferences/UserPreferencesSchema.ts'
import { UserGender } from '@/core/domain/resources/enums/UserGender.ts'
import { LocalUser, type UserView, type View } from '@/core/domain/users/LocalUser.ts'
import { EntityValidator } from '@/core/infrastructure/utils/EntityValidator.ts'
import { UserIdentifierSchema } from '@/core/infrastructure/schemas/utils/UserIdentifierSchema.ts'
import { UserEmailSchema } from '@/core/infrastructure/schemas/utils/UserEmailSchema.ts'
import {
  UserBirthdateSchema
} from '@/core/infrastructure/schemas/utils/UserBirthdateSchema.ts'
import { UserGenderSchema } from '@/core/infrastructure/schemas/utils/UserGenderSchema.ts'
import { UserListRolesSchema } from '@/core/infrastructure/schemas/utils/UserListRolesSchema.ts'

/**
 * Représentation complète d'un utilisateur
 */
export const LocalUserSchema = z.object({
  identifier: UserIdentifierSchema,
  firstname: z.string().min(1).max(150),
  lastname: z.string().min(1).max(150),
  username: z.string().min(3).max(128).optional(),
  email: UserEmailSchema.optional(),
  birthdate: UserBirthdateSchema.nullish(),
  gender: UserGenderSchema.optional(),
  roles: UserListRolesSchema,
  picture: z.string().nullable(),
  blocked: z.boolean().optional(),
  emailVerified: z.boolean().optional(),
  lastLogin: z.string().datetime().nullish(),
  loginCount: z.number().int().nonnegative().optional(),
  preferences: UserPreferencesSchema.optional(),
  hasAnsweredTrackingConsentBanner: z.boolean().optional(),
  hasConsentedForTracking: z.boolean().optional(),
  isRegisterProcessDone: z.boolean().optional(),
  createdAt: z.string().datetime().nullish(),
  modifiedAt: z.string().datetime().nullish(),
})
export type LocalUserEntity = z.infer<typeof LocalUserSchema>

/**
 * Projection de la View.Public d'un utilisateur
 */
export const LocalUserPublicViewSchema = LocalUserSchema.pick({
  identifier: true,
  firstname: true,
  lastname: true,
  picture: true,
  roles: true
}).strict()

/**
 * Projection de la View.Staff d'un utilisateur
 */
export const LocalUserStaffViewSchema = LocalUserSchema.pick({
  identifier: true,
  firstname: true,
  lastname: true,
  picture: true,
  roles: true,
  username: true,
  email: true,
  gender: true,
  createdAt: true,
  modifiedAt: true
})
  .extend({
    username: z.string(),
    email: z.string(),
    gender: z.nativeEnum(UserGender.Values),
    createdAt: z.string().datetime().nullable(),
    modifiedAt: z.string().datetime().nullable()
  })
  .strict()

/**
 * Projection de la View.Self d'un utilisateur
 */
export const LocalUserSelfViewSchema = LocalUserSchema.pick({
  identifier: true,
  firstname: true,
  lastname: true,
  picture: true,
  roles: true,
  username: true,
  email: true,
  gender: true,
  createdAt: true,
  modifiedAt: true,
  birthdate: true,
  preferences: true,
  hasAnsweredTrackingConsentBanner: true,
  hasConsentedForTracking: true,
  isRegisterProcessDone: true
})
  .extend({
    username: z.string(),
    email: z.string(),
    gender: z.nativeEnum(UserGender.Values),
    createdAt: z.string().datetime().nullable(),
    modifiedAt: z.string().datetime().nullable(),
    birthdate: z.string().date().nullable(),
    preferences: UserPreferencesSchema,
    hasAnsweredTrackingConsentBanner: z.boolean(),
    hasConsentedForTracking: z.boolean(),
    isRegisterProcessDone: z.boolean()
  })
  .strict()

/**
 * Projection de la View.Admin d'un utilisateur
 */
export const LocalUserAdminViewSchema = LocalUserSchema.pick({
  identifier: true,
  firstname: true,
  lastname: true,
  picture: true,
  roles: true,
  username: true,
  email: true,
  gender: true,
  createdAt: true,
  modifiedAt: true,
  birthdate: true,
  preferences: true,
  hasAnsweredTrackingConsentBanner: true,
  hasConsentedForTracking: true,
  isRegisterProcessDone: true,
  blocked: true,
  emailVerified: true,
  lastLogin: true,
  loginCount: true
})
  .extend({
    username: z.string(),
    email: z.string(),
    gender: z.nativeEnum(UserGender.Values),
    createdAt: z.string().datetime().nullable(),
    modifiedAt: z.string().datetime().nullable(),
    birthdate: z.string().date().nullable(),
    preferences: UserPreferencesSchema,
    hasAnsweredTrackingConsentBanner: z.boolean(),
    hasConsentedForTracking: z.boolean(),
    isRegisterProcessDone: z.boolean(),
    blocked: z.boolean(),
    emailVerified: z.boolean(),
    lastLogin: z.string().datetime().nullable(),
    loginCount: z.number().int().nonnegative()
  })
  .strict()

/**
 * Couplage View <-> Schéma
 */
const SCHEMAS: Record<View, z.ZodTypeAny> = {
  public: LocalUserPublicViewSchema,
  staff: LocalUserStaffViewSchema,
  self: LocalUserSelfViewSchema,
  admin: LocalUserAdminViewSchema
}

/**
 * Factory permettant de créer des instances de LocalUser en fonction de la View
 * @Deprecated - Utilise mapper/users/LocalUserFactory.ts
 */
export class LocalUserFactory {
  static create(data: unknown, view: View = 'public'): LocalUser & UserView {
    return EntityValidator.validateAndCreateFactory(
      SCHEMAS[view],
      data,
      (data) => new LocalUser(data, view),
      'LocalUserFactory::create'
    )
  }
}